<template>
  <context-bar-and-content class="office-details" v-loading="isLoading" :display-footer="false">
    <template slot="context-bar-left">
      <el-button icon="el-icon-arrow-left" @click="navToUserList">Return to User List</el-button>
    </template>
    <template slot="context-bar-middle" v-if="this.user">
      {{ this.user.fullName }}
    </template>
    <template slot="context-bar-right">
      &nbsp;
    </template>
    <div class="main-section">
      <el-row :gutter="18">
        <el-col :md="24" class="mb-3">
          <el-card class="box-card" :body-style="{ padding: '20px 20px 2px 20px' }">
            <div slot="header" class="clearfix detail-header">
              <i class="fa fa-user"></i>User Details
              <el-button type="primary" icon="fa fa-asterisk" class="mr-3" @click="changePassword">Change
                Password</el-button>
              <el-button type="primary" icon="fa fa-pencil-alt" class="mr-3" @click="editUser">Edit</el-button>
            </div>
            <el-row :gutter="18" justify="start">
              <el-col :sm="12" :lg="6">
                <static-field label="Department" :value="departmentName"></static-field>
              </el-col>
              <el-col :sm="12" :lg="6">
                <static-field label="Name" :value="user.fullName"></static-field>
              </el-col>
              <el-col :sm="12" :lg="6">
                <static-field label="Username" :value="user.username"></static-field>
              </el-col>
              <el-col :sm="12" :lg="6">
                <static-field label="Active" :value="isActive"></static-field>
              </el-col>
              <el-col :sm="12" :lg="6">
                <static-field label="Email" :value="user.email"></static-field>
              </el-col>
              <el-col :sm="12" :lg="6">
                <static-field label="Phone" :value="user.phone"></static-field>
              </el-col>
            </el-row>
          </el-card>
        </el-col>
      </el-row>
      <el-row :gutter="18">
        <el-col :md="24" class="mb-3">
          <el-card class="box-card" :body-style="{ padding: '0px' }">
            <div slot="header" class="clearfix detail-header">
              <i class="fa fa-sitemap"></i>User Offices
              <el-button type="primary" icon="fa fa-pencil-alt" class="mr-3" @click="editUserOffices"
                v-if="!isAdmin">Edit</el-button>
            </div>
            <p class="ma-3" v-if="isAdmin"><strong>ADMIN's implicitly have access to all offices.</strong></p>
            <template v-else>
              <el-alert title="WARNING" type="warning"
                description="The user will not be able to login because they do not have any offices associated with their account. Click the Edit button to assign offices to this user."
                show-icon :closable="false"
                v-show="user.allowedOffices === undefined || user.allowedOffices.length === 0">
              </el-alert>
              <user-offices-table :office-ids="user.allowedOffices"></user-offices-table>
            </template>
          </el-card>
        </el-col>
      </el-row>
      <el-row v-loading="docutaxNotificationSetting.loading" :gutter="18">
        <el-col :md="24" class="mb-3">
          <el-card class="box-card" :body-style="{ padding: '0px' }">
            <div slot="header" class="clearfix detail-header">
              <i class="fa fa-envelope"></i>Docutax Notifications
            </div>
            <el-form class="ma-3" ref="docutax-notification-form" label-position="top">
              <el-form-item label="Receive notifications for:">
                <el-checkbox v-model="docutaxNotificationSetting.data.subscribeToResponses">Replies to my
                  Requests</el-checkbox>
                <el-checkbox v-model="docutaxNotificationSetting.data.subscribeToAllSubmissions">Any submission to my
                  office(s)</el-checkbox>
              </el-form-item>
              <el-form-item label="How would you like to be notified?">
                <el-checkbox v-model="docutaxNotificationSetting.data.receiveTextNotification">Text
                  Message</el-checkbox>
                <el-checkbox v-model="docutaxNotificationSetting.data.receiveEmailNotification">Email</el-checkbox>
              </el-form-item>
              <el-button :disabled="docutaxNotificationSetting.saving" size="small" type="primary"
                @click="saveDocutaxNotificationSetting">Save Notification
                Preferences</el-button>
            </el-form>
          </el-card>
        </el-col>
      </el-row>
    </div>
    <el-dialog title="Update User Details" :visible.sync="userUpdateDialog.visible" :close-on-click-modal="false"
      width="60%">
      <user-update-dialog @close="userUpdateDialog.visible = false" :user="userUpdateDialog.user"
        @userUpdated="onUserUpdated"></user-update-dialog>
    </el-dialog>
    <el-dialog title="Change Password" :visible.sync="changePasswordDialogVisible" :close-on-click-modal="false"
      width="30%">
      <user-change-password-dialog @close="changePasswordDialogVisible = false"
        :user="user"></user-change-password-dialog>
    </el-dialog>
    <el-dialog title="Update Allowed Offices" :visible.sync="updateAllowedOfficesDialogVisible"
      :close-on-click-modal="false" width="850px">
      <user-edit-allowed-offices-dialog @close="updateAllowedOfficesDialogVisible = false" :user="user"
        @userUpdated="onUserUpdated"></user-edit-allowed-offices-dialog>
    </el-dialog>
  </context-bar-and-content>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import get from 'lodash/get';
import ContextBarAndContent from '@/components/layout/ContextBarAndContent.vue';
import api from '@/api';
import User from '@/model/User';
import DocutaxNotificationSetting from '@/model/DocutaxNotificationSetting';
import StaticField from '@/components/widgets/StaticField.vue';
import UserOfficesTable from '@/components/tables/UserOffices.vue';
import UserUpdateDialog from '@/components/dialog-content/UserUpdate.vue';
import UserChangePasswordDialog from '@/components/dialog-content/UserChangePassword.vue';
import UserEditAllowedOfficesDialog from '@/components/dialog-content/UserEditAllowedOffices.vue';
import { DEPARTMENT } from '@/enum/UserDepartments';

export default {
  data() {
    return {
      isLoading: false,
      user: new User(),
      docutaxNotificationSetting: {
        loading: false,
        data: new DocutaxNotificationSetting(),
        saving: false,
      },
      userOffices: [],
      userUpdateDialog: {
        visible: false,
        user: new User(),
      },
      changePasswordDialogVisible: false,
      updateAllowedOfficesDialogVisible: false,
    };
  },
  watch: {
    '$route.params.userId': {
      immediate: true,
      async handler(userId) {
        this.fetchUser(userId);
        this.loadDocutaxNotificationSettings(userId);
      },
    },
  },
  methods: {
    ...mapActions('Lookup', ['getDepartments']),
    async fetchUser(userId) {
      try {
        this.isLoading = true;
        const response = await api.user.get(userId);
        this.user = new User(response.data);
      } catch (e) {
        this.$notify({
          type: 'error',
          title: 'Error',
          message: e.message || 'An error occurred while loading the user.',
        });
      } finally {
        this.isLoading = false;
      }
    },
    async loadDocutaxNotificationSettings(userId) {
      try {
        this.docutaxNotificationSetting.loading = true;
        const response = await api.docutaxNotificationSetting.findByUserId(userId);
        this.docutaxNotificationSetting.data = new DocutaxNotificationSetting(response.data);
      } catch (e) {
        this.$notify({
          type: 'error',
          title: 'Error',
          message: e.message || 'An error occurred while loading the user\'s Docutax Notification Settings.',
        });
      } finally {
        this.docutaxNotificationSetting.loading = false;
      }
    },
    async saveDocutaxNotificationSetting() {
      try {
        this.docutaxNotificationSetting.saving = true;
        const response = await api.docutaxNotificationSetting.save(this.user.id, this.docutaxNotificationSetting.data);
        this.docutaxNotificationSetting.data = new DocutaxNotificationSetting(response.data);
        this.$message.success('Successfully saved notification preferences');
      } catch (e) {
        this.$notify({
          type: 'error',
          title: 'Error',
          message: e.message || 'An error occurred while saving the user\'s Docutax Notification Settings.',
        });
      } finally {
        this.docutaxNotificationSetting.saving = false;
      }
    },
    editUser() {
      this.userUpdateDialog.visible = true;
      this.userUpdateDialog.user = new User(this.user);
    },
    editUserOffices() {
      this.updateAllowedOfficesDialogVisible = true;
    },
    changePassword() {
      this.changePasswordDialogVisible = true;
    },
    navToUserList() {
      this.$router.push({ name: 'user-list' });
    },
    onUserUpdated(user) {
      this.user = user;
    },
  },
  computed: {
    ...mapGetters('Lookup', ['departmentMap']),
    departmentName() {
      if (this.user.departmentId) {
        return get(this.departmentMap, `${this.user.departmentId}.name`, '');
      }
      return '';
    },
    isActive() {
      if (this.user.active !== undefined) {
        return this.user.active ? 'Yes' : 'No';
      }
      return '';
    },
    isAdmin() {
      const departmentId = this.user.departmentId;
      if (departmentId !== undefined) {
        return (departmentId === DEPARTMENT.ADMIN || departmentId === DEPARTMENT.SUPER_ADMIN);
      }
      return false;
    },
  },
  created() {
    this.getDepartments();
  },
  components: {
    ContextBarAndContent,
    StaticField,
    UserOfficesTable,
    UserUpdateDialog,
    UserChangePasswordDialog,
    UserEditAllowedOfficesDialog,
  },
};

</script>

<style lang="scss" scoped>
.office-details {
  .main-section {
    width: calc(100% - 18px);
    margin-top: 18px;
  }

  .detail-header {
    font-size: 18px;
    font-weight: bold;
    line-height: 24px;

    button {
      float: right;
      padding: 5px 5px;
    }
  }
}
</style>
